import { CheckCircle2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { createPlaceholderFiles } from "../../helpers/file_helpers";
import { useUploadByLink } from "../../hooks/useUploadByLink";
import {
  closeExternalLinkModal,
  openExternalLinkModal,
  setExpanded,
  setFiles,
  setUploadConfiguration,
  setVisibilty
} from "../../redux/file-upload/fileUploadSlice";
import { uploadFileSelectors } from "../../redux/file-upload/selectors";
import { showSubframeToast } from "./CasePrepToast";

const MAX_LINKS_DEFAULT = 10;

type LinkField = {
  id: string;
  value: string;
};

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// extracted: createPlaceholderFiles

const UploadExternalLinkModal = () => {
  const { id: uid } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { externalLinkModal } = useSelector(uploadFileSelectors.selectAll);
  const {
    isOpen,
    storagePath,
    collectionPath,
    key: modalKey
  } = externalLinkModal;
  const canShowExternalLinkModal = /\/(individual|admin|studio)\//i.test(
    location.pathname
  );
  const fieldIdPrefix = useId();
  const onClose = () => dispatch(closeExternalLinkModal());
  const maxLinks = MAX_LINKS_DEFAULT;
  const [links, setLinks] = useState<LinkField[]>([
    { id: generateId(), value: "" }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [succeeded, setSucceeded] = useState<Set<string>>(new Set());
  const suppressResetRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      if (suppressResetRef.current) {
        suppressResetRef.current = false;
        return;
      }
      setLinks([{ id: generateId(), value: "" }]);
      setSucceeded(new Set());
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setLinks([{ id: generateId(), value: "" }]);
    setSucceeded(new Set());
    setErrors({});
    setIsSubmitting(false);
    suppressResetRef.current = false;
  }, [modalKey]);

  const canAddMore = links.length < maxLinks;
  const hasAnyValue = useMemo(
    () => links.some((l) => l.value.trim().length > 0),
    [links]
  );

  const nonEmptyIds = useMemo(
    () => links.filter((l) => l.value.trim().length > 0).map((l) => l.id),
    [links]
  );
  const hasSuccess = useMemo(() => succeeded.size > 0, [succeeded]);
  const retrievedItems = useMemo(
    () => links.filter((l) => succeeded.has(l.id)),
    [links, succeeded]
  );
  const pendingItems = useMemo(
    () =>
      links
        .map((l, i) => ({ ...l, index: i }))
        .filter((l) => !succeeded.has(l.id)),
    [links, succeeded]
  );

  const handleAdd = () => {
    if (!canAddMore) return;
    setLinks((prev) => {
      const next = [...prev, { id: generateId(), value: "" }];
      return next;
    });
  };

  const handleChange = (index: number, value: string) => {
    setLinks((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value } : item))
    );
    setErrors((prev) => {
      const id = links[index]?.id;
      if (!id || prev[id] === undefined) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleRemove = (index: number) => {
    const id = links[index]?.id;
    setLinks((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      if (!id) return prev;
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setSucceeded((prev) => {
      if (!id) return prev;
      const next = new Set(Array.from(prev));
      next.delete(id);
      return next;
    });
  };

  const { uploadByLinks } = useUploadByLink();

  const showReopenToast = (
    opts: {
      status: "info" | "success" | "warning" | "error";
      title: string;
      description?: string;
      duration?: number;
    } = { status: "info", title: "" }
  ) => {
    const toastVisuals = {
      info: { variant: "brand" as const, icon: "FeatherInfo" as const },
      success: { variant: "success" as const, icon: "FeatherCheck" as const },
      warning: {
        variant: "neutral" as const,
        icon: "FeatherAlertTriangle" as const
      },
      error: { variant: "error" as const, icon: "FeatherAlertCircle" as const }
    }[opts.status];

    showSubframeToast({
      ...toastVisuals,
      title: opts.title,
      description: opts.description,
      durationMs: opts.duration ?? 6000,
      actions: (
        <button
          type="button"
          className="text-sm font-medium text-accent-seed hover:underline"
          onClick={() => {
            dispatch(
              openExternalLinkModal({
                storagePath,
                collectionPath
              })
            );
          }}
        >
          View Details
        </button>
      )
    });
  };

  const handleUpload = async () => {
    const entries = links
      .map((l, i) => ({ index: i, id: l.id, url: l.value.trim() }))
      .filter((e) => !succeeded.has(e.id))
      .filter((e) => e.url.length > 0)
      .slice(0, maxLinks);

    const payload = entries.map((e) => e.url);

    if (payload.length === 0) {
      onClose();
      return;
    }

    const placeholderFiles = createPlaceholderFiles(payload);
    if (placeholderFiles.length > 0) {
      dispatch(
        setUploadConfiguration({
          uid: String(uid ?? ""),
          path: storagePath,
          collectionPath,
          source: "external-link"
        })
      );
      dispatch(setFiles(placeholderFiles));
      dispatch(setVisibilty(true));
      dispatch(setExpanded(true));
    }

    setIsSubmitting(true);
    setErrors({});
    try {
      const TIMEOUT_MS = 3000;
      const uploadPromise = uploadByLinks(payload);
      const timeoutPromise = new Promise<"timeout">((resolve) => {
        setTimeout(() => resolve("timeout"), TIMEOUT_MS);
      });

      const winner = await Promise.race([uploadPromise, timeoutPromise]);

      if (winner === "timeout") {
        setIsSubmitting(false);
        suppressResetRef.current = true;
        onClose();
        showReopenToast({
          status: "info",
          title: `${payload.length} file(s) uploading...`,
          duration: 5000
        });

        uploadPromise
          .then(({ results }) => {
            const nextErrors: Record<string, string> = {};
            const newlySucceeded = new Set<string>();

            results.forEach((r, i) => {
              const entryRef = entries[i];
              const originalId =
                entryRef?.id ?? links[entryRef?.index ?? i]?.id;
              if (r.error) {
                if (originalId) {
                  nextErrors[originalId] =
                    r.error ||
                    "This link couldn’t be accessed. Please check the URL and try again.";
                }
              } else if (originalId) {
                newlySucceeded.add(originalId);
              }
            });

            if (Object.keys(nextErrors).length > 0) {
              setErrors(nextErrors);
            }

            if (newlySucceeded.size > 0) {
              setSucceeded((prev) => {
                const combined = new Set(Array.from(prev));
                for (const id of Array.from(newlySucceeded)) combined.add(id);
                return combined;
              });
            }

            const numErrors = results.filter((r) => r.error).length;
            const numSuccess = results.length - numErrors;

            if (numSuccess > 0) {
              showReopenToast({
                status: numErrors > 0 ? "warning" : "success",
                title:
                  numErrors > 0
                    ? `${numSuccess} files were uploaded successfully. ${numErrors} could not be loaded.`
                    : `All files uploaded successfully.`
              });
            } else if (numErrors > 0) {
              showReopenToast({
                status: "error",
                title: "Upload failed: check URL links and try again.",
                duration: 7000
              });
            }
          })
          .catch(() => {
            showReopenToast({
              status: "error",
              title:
                "An error occurred while retrieving files. Please try again.",
              duration: 7000
            });
          });

        return;
      }

      const { results } = winner as Awaited<ReturnType<typeof uploadByLinks>>;

      const nextErrors: Record<string, string> = {};
      const newlySucceeded = new Set<string>();
      results.forEach((r, i) => {
        const entryRef = entries[i];
        const originalId = entryRef?.id ?? links[entryRef?.index ?? i]?.id;
        if (!originalId) return;
        if (r.error) {
          nextErrors[originalId] =
            r.error ||
            "This link isn't valid or couldn't be reached. Please check the URL and try again.";
        } else {
          newlySucceeded.add(originalId);
        }
      });

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
      }

      if (newlySucceeded.size > 0) {
        setSucceeded((prev) => {
          const combined = new Set(Array.from(prev));
          for (const idx of Array.from(newlySucceeded)) combined.add(idx);
          return combined;
        });
      }

      const nonEmptyIdsNow = links
        .filter((l) => l.value.trim().length > 0)
        .map((l) => l.id);
      const allSucceeded = nonEmptyIdsNow.every((id) =>
        newlySucceeded.size > 0
          ? succeeded.has(id) || newlySucceeded.has(id)
          : succeeded.has(id)
      );
      if (allSucceeded && Object.keys(nextErrors).length === 0) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen && canShowExternalLinkModal}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Using External Link</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {hasSuccess && (
            <>
              <div>
                <p className="mb-2 text-sm font-medium text-success">
                  Retrieved
                </p>
                <div className="flex flex-col gap-1">
                  {retrievedItems.map((item) => (
                    <div
                      key={`ok-${item.id}`}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle2 className="size-3.5 shrink-0 text-success" />
                      <span className="truncate text-sm text-fg-3">
                        {item.value.trim()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <Separator />
            </>
          )}

          {pendingItems.map((field, visibleIndex) => {
            const inputId = `${fieldIdPrefix}-${field.id}`;
            const error = errors[field.id];
            return (
              <div
                key={field.id}
                className="flex flex-col items-start gap-1.5 duration-150 animate-in fade-in-0 slide-in-from-bottom-1"
              >
                <Label htmlFor={inputId}>{`Link ${visibleIndex + 1}`}</Label>
                <div className="relative w-full">
                  <Input
                    id={inputId}
                    placeholder="Paste a link here"
                    value={field.value}
                    onChange={(e) => handleChange(field.index, e.target.value)}
                    aria-invalid={error !== undefined}
                    className="pr-9"
                  />
                  {field.index > 0 ? (
                    <Button
                      aria-label="Remove link"
                      variant="ghost"
                      size="icon-xs"
                      className="absolute top-1/2 right-0.5 -translate-y-1/2"
                      onClick={() => handleRemove(field.index)}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>
                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            );
          })}

          {maxLinks > 1 && (
            <Button
              variant="ghost"
              onClick={handleAdd}
              disabled={!canAddMore}
              className="self-center"
            >
              <Plus />
              Add Link
            </Button>
          )}
          {maxLinks > 1 && !canAddMore && (
            <p className="text-sm text-fg-3">
              Maximum {maxLinks} links reached
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!hasAnyValue || isSubmitting}
          >
            {isSubmitting ? <Spinner /> : null}
            {hasSuccess || Object.keys(errors).length > 0
              ? "Retry Failed"
              : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadExternalLinkModal;
